import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  getFirestore,
  Firestore,
  doc,
  collection,
  addDoc,
  getDoc,
  query,
  onSnapshot,
  updateDoc,
  deleteDoc,
  orderBy,
  where,
  getDocs,
  limit,
  startAfter,
  startAt,
  endAt,
  QueryDocumentSnapshot,
  DocumentData,
  setDoc,
} from "firebase/firestore";

import { app, functions } from "../fb/config";

import { Messaging, getMessaging } from "firebase/messaging";
import { EmailAuthProvider, onAuthStateChanged, reauthenticateWithCredential, updateEmail, updatePassword, UserCredential } from "firebase/auth";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

import { getAuth, createUserWithEmailAndPassword, signOut, signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";

import { IUser } from "../interfaces/IUser";
import { IUserNotis } from "../interfaces/IUserNotis";
import { IGlobalNotification } from "../interfaces/IGlobalNotification";
import { normalizeString, sliceIntoChunks } from "../utils/functions";
import { httpsCallable } from "firebase/functions";
import moment from "moment";

export interface IFirebaseContextProps {
  user: IUser;
  permissions: boolean;
  messaging: Messaging;
  allUsers: IUser[];
  deleteNotifications: (notisToUpdate: any) => void;
  getUserData: (id: string) => Promise<IUser | null>;
  loadingUser: boolean;
  updateUser: (data: IUser) => Promise<void>;
  uploadImage: (file: any) => Promise<string>;
  signup: (name: string, email: string, password: string, businessId: string, local: string) => Promise<UserCredential>;
  signout: () => Promise<void>;
  updateEmailAuth: (email: string) => Promise<void>;
  login: (email: string, password: string) => Promise<UserCredential>;
  db: Firestore;
  progresspercent: number;
  sendingPasswordResetMail: (value: string) => Promise<string>;
  updateActualPassword: (password: string, actualPassword: string, email: string) => Promise<void>;
  setNotification: React.Dispatch<any>;
  notification: {
    title: string;
    body: string;
  };
  tokenMessaging: string;
  userNotis: IUserNotis;
  updateNotifications: (notisToUpdate: any) => void;
  getUsersByBusinessId: (id: string) => Promise<IUser[]>;
  getFirebaseAllUsers: () => Promise<QueryDocumentSnapshot<DocumentData>[]>;
  getFirebaseNextUsers: (id: any) => Promise<QueryDocumentSnapshot<DocumentData>[]>;
  getAllUsersByName: (nameInit: string) => Promise<QueryDocumentSnapshot<DocumentData>[]>;
  loadAllUsers: () => Promise<IUser[]>;
  loadAllUsersForAdmin: () => Promise<void>;
  addNotiEventBmc: (event: any, type: string, businnesName?: string) => Promise<void>;
  createGlobalNotification: (type: string, title: string, description?: string, image?: string, data?: any, expiresAt?: number, businessId?: string) => Promise<void>;
  updateAllUsers: any;
  isMaintenanceMode: boolean;
  setMaintenanceMode: (enabled: boolean) => Promise<void>;
  globalNotifications: IGlobalNotification[];
  markGlobalNotificationAsViewed: (notificationId: string) => Promise<void>;
  deleteGlobalNotificationForUser: (notificationId: string) => Promise<void>;
}

export const FirebaseContext = createContext<IFirebaseContextProps>(null);

export const FirebaseProvider: any = ({ children }: any) => {
  // const { user, loadingUser } = useAuth();
  const [user, setUser] = useState<IUser>();
  const [loadingUser, setLoadingUser] = useState(true);
  const [allUsers, setAllUsers] = useState<IUser[]>([]);
  const [allUsersLoaded, setAllUsersLoaded] = useState(false); // Para evitar recargas
  const [permissions, setPermissions] = useState(false);
  const [tokenMessaging, setTokenMessaging] = useState(null);
  const [userNotis, setUserNotis] = useState<IUserNotis>(null);
  const [globalNotifications, setGlobalNotifications] = useState<IGlobalNotification[]>([]);
  const [notification, setNotification] = useState<{
    title: string;
    body: string;
  }>(null);
  const [isMaintenanceMode, setIsMaintenanceModeState] = useState(false);
  // console.log(user) // ✅ Comentado para evitar renders innecesarios
  const messaging = getMessaging();

  const db = getFirestore(app);
  const storage = getStorage(app);

  const auth = getAuth();

  const [progresspercent, setProgresspercent] = useState(0);

  // Suscripción al estado de mantenimiento
  useEffect(() => {
    const maintenanceDocRef = doc(db, "config", "maintenance");
    const unsubscribe = onSnapshot(maintenanceDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setIsMaintenanceModeState(docSnap.data().enabled || false);
      } else {
        setIsMaintenanceModeState(false);
      }
    });
    return () => unsubscribe();
  }, [db]);

  useEffect(() => {
    const fetchUserData = async (userAuth) => {
      setLoadingUser(true);

      try {
        if (!userAuth) {
          setUser(null);
          localStorage.removeItem("token");
          return;
        }
  
        localStorage.setItem("token", userAuth.accessToken);  
        // ✅ Intentamos obtener usuario desde localStorage
        const cachedUser = localStorage.getItem(`user_${userAuth.uid}`);
        if (cachedUser) {
          setUser(JSON.parse(cachedUser));
          return;
        }

        // ✅ Si no está en cache, buscamos en Firestore
        const userRef = query(collection(db, "users"), where("uid", "==", userAuth.uid));
        const userSnapshot = await getDocs(userRef);

        if (!userSnapshot.empty) {
          const userData = userSnapshot.docs[0].data();
          const userDB: IUser = {
            id: userSnapshot.docs[0].id,
            uid: userAuth.uid,
            businessId: userData.businessId || null,
            local: userData.local || null,
            name: userData.name || "",
            email: userData.email || "",
            image: userData.image || "",
            firstTime: userData.firstTime ?? false,
            responsable: userData.responsable ?? false,
            acceptLegal: userData.acceptLegal ?? false,
            nameToFilter: normalizeString(userData.name || ""), // ✅ Se añade nameToFilter
            roles: {
              canteen: userData.roles?.canteen ?? false,
              maintenance: userData.roles?.maintenance ?? false,
              parcel: userData.roles?.parcel ?? false,
              gym: userData.roles?.gym ?? false,
              super: userData.roles?.super ?? false,
              cleaner: userData.roles?.cleaner ?? false,
              god: userData.roles?.god ?? false,
            },
          };

          // ✅ Guardamos en cache para evitar llamadas repetidas
          localStorage.setItem(`user_${userAuth.uid}`, JSON.stringify(userDB));
          setUser(userDB);
        }
      } catch (error) {
        console.error("⚠️ Error obteniendo usuario:", error);
      } finally {
        setLoadingUser(false);
      }
    };

    // ✅ Suscribimos al estado de autenticación
    const unsubscribe = onAuthStateChanged(auth, fetchUserData);
    return () => unsubscribe();
  }, []);

  const getFirebaseAllUsers = async () => {
    const q = query(collection(db, "users"), orderBy("nameToFilter", "asc"), limit(25));
    const snap = await getDocs(q);
    const users = snap.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
    setAllUsers(users);
    return snap.docs; // 👈 devolvemos docs
  };

  const getFirebaseNextUsers = async (lastDoc) => {
    if (!lastDoc) return [];
    const q = query(collection(db, "users"), orderBy("nameToFilter", "asc"), startAfter(lastDoc), limit(25));
    const snap = await getDocs(q);
    const newUsers = snap.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
    setAllUsers((prev) => [...prev, ...newUsers]);
    return snap.docs; // 👈 devolvemos docs
  };

  const getAllUsersByName = async (nameInit: string) => {
    const q = query(
      collection(db, "users"), 
      orderBy("nameToFilter", "asc"), 
      startAt(nameInit), 
      endAt(nameInit + "\uf8ff"), 
      limit(25)
    );
    const snap = await getDocs(q);
    const filtered = snap.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
    setAllUsers(filtered);
    return snap.docs; // 👈 devolvemos docs
  };

  const loadAllUsers = async () => {
    const q = query(collection(db, "users"), orderBy("nameToFilter", "asc"));
    const snap = await getDocs(q);
    const users = snap.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
    setAllUsers(users);
    return users;
  };

  // 🎯 OPTIMIZACIÓN: Carga única de todos los usuarios para administradores
  const loadAllUsersForAdmin = async () => {
    // Si ya están cargados, no hacer nada
    if (allUsersLoaded) {
      return;
    }

    const q = query(collection(db, "users"), orderBy("nameToFilter", "asc"));
    const snap = await getDocs(q);
    const users = snap.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
    setAllUsers(users);
    setAllUsersLoaded(true);
  };

  const getFirebaseAllUsers2 = async () => {
    const q = query(collection(db, "users"));
    const usersDocs = await getDocs(q);

    const users: IUser[] = usersDocs.docs.map((doc: any) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return users;
  };

  const addNotiEventBmc = async (eventData, type, name) => {
    // 🚀 FASE 2: Usar notificaciones globales en vez de crear 200k documentos
    // Calcula la fecha de expiración (30 días después del evento si aplica)
    const expiresAt = eventData.finishTimestamp 
      ? eventData.finishTimestamp + (30 * 24 * 60 * 60 * 1000) 
      : null;

    // 1. Crear notificación global (independiente del email)
    try {
      await createGlobalNotification(
        type === "events-bmc" ? "event-bmc" : "event-business",
        eventData.title,
        eventData.description || `Nuevo evento de ${name || "BMC"}`,
        eventData.image,
        eventData, // Todo el objeto del evento
        expiresAt,
        eventData.businessId || null
      );
      console.log("✅ Notificación global creada con éxito.");
    } catch (error) {
      console.error("Error creando notificación global:", error);
    }

    // 2. Enviar email a través de Mailchimp (independiente de la notificación)
    try {
      await sendMailWithMailchimp({ ...eventData, businessName: name ? name : "BMC" });
      console.log("✅ Correo enviado con éxito desde Mailchimp.");
    } catch (error) {
      console.error("Error enviando el correo:", error);
    }
  };

  // Llama a Firebase Function para enviar el correo
  const sendMailWithMailchimp = async (eventData) => {
    const sendMailEventMailchimp = httpsCallable(functions, "sendMailEventMailchimp");

    try {
      // Enviar datos a la función de Firebase
      const result: any = await sendMailEventMailchimp({
        event: {
          title: eventData.title,
          description: eventData.description,
          description2: eventData?.description2,
          description3: eventData?.description3,
          externalUrl: eventData?.externalUrl,
          local: eventData?.local,
          phone: eventData?.phone,
          date: `${moment(eventData?.initTimestamp).format("DD/MM/YYYY")}–${moment(eventData?.finishTimestamp).format("DD/MM/YYYY")}`,
          image: eventData.image,
          businessName: eventData?.businessName,
        },
        userName: "",
        comments: "",
      });

      if (result.data.success) {
        console.log("Correo enviado con éxito:", result.data.message);
      } else {
        console.error("Error al enviar correo:", result.data);
      }
    } catch (error) {
      console.error("Error llamando a Firebase Function:", error);
    }
  };

  const updateAllUsers = async () => {
    const next = query(collection(db, "users"));
    const docSnap: any = await getDocs(next);

    if (docSnap.docs.length > 0) {
      return await Promise.all(
        docSnap.docs.map(async (doc, idx) => {
          const nameFormatted = normalizeString(doc.data().name);
          const data = { ...doc.data(), id: doc.id, nameToFilter: nameFormatted };
          await updateUser(data);

          return data;
        }),
      );
    } else {
      console.log("Users not found");
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, `gym-events/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          // setProgresspercent(progress); // Asegúrate de que esta función esté definida y acepte un número
          console.log(`Upload is ${snapshot.state}, ${progress}% done`);
        },
        (error) => {
          console.error(error);
          reject(error); // Podrías considerar pasar el error a reject para manejarlo mejor
        },
        async () => {
          getDownloadURL(uploadTask.snapshot.ref)
            .then((downloadURL) => {
              resolve(downloadURL);
            })
            .catch((error) => reject(error));
        },
      );
    });
  };
  const checkRoles = useCallback(() => {
    if (user?.roles?.super || user?.roles?.canteen || user?.roles?.gym || user?.roles?.parcel || user?.roles?.maintenance) {
      setPermissions(true);
    } else setPermissions(false);
  }, [user?.roles]);

  const updateUser = async (data: IUser) => {
    try {
      // 1. Actualiza el documento en Firestore
      await updateDoc(doc(db, "users", data.id), { ...data });

      // 2. Solo actualizar el estado global si estamos editando nuestro propio perfil
      // Comparamos el id del documento actualizado con el id del usuario actual
      if (user?.id === data.id) {
        const updatedDoc = await getDoc(doc(db, "users", data.id));
        if (updatedDoc.exists()) {
          const updatedUser = { id: updatedDoc.id, ...(updatedDoc.data() as IUser) };
          setUser(updatedUser);
          
          // ✅ Actualizar también el cache de localStorage para mantener consistencia
          if (updatedUser.uid) {
            localStorage.setItem(`user_${updatedUser.uid}`, JSON.stringify(updatedUser));
          }
        }
      }
      // Si es un admin editando a otro usuario, solo actualizamos Firestore, no el estado global
    } catch (error) {
      console.log(error);
    }
  };

  const signup = async (name: string, email: string, password: string, businessId: string, local: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    const userData = {
      uid: user.uid,
      name: name,
      nameToFilter: normalizeString(name),
      email: user.email,
      businessId: businessId,
      local: local,
      roles: { canteen: false, maintenance: false, gym: false, parcel: false },
    };

    const usersRef = collection(db, "users");
    await addDoc(usersRef, userData);

    return userCredential;
  };
  const getUserData = async (id: string) => {
    try {
      const docRef = doc(db, "users", id);
      const docSnap: any = await getDoc(docRef);

      if (docSnap.exists()) {
        const user: IUser = { id: docSnap.id, ...docSnap.data() };
        return user;
      }
      
      console.warn(`User not found: ${id}`);
      return null;
    } catch (error) {
      console.error(`Error fetching user ${id}:`, error);
      return null;
    }
  };

  const getUsersByBusinessId = async (id: string) => {
    try {
      const q = query(collection(db, "users"), where("businessId", "==", id));
      const docSnap: any = await getDocs(q);
      if (docSnap.docs.length > 0) {
        const businessUsers: IUser[] = await Promise.all(
          docSnap.docs.map((doc) => {
            return { id: doc.id, ...doc.data() };
          }),
        );
        return businessUsers;
      } else {
        console.log("No such document!");
      }
    } catch (error) {}
  };

  const login = async (email: string, password: string) => {
    try {
      const loging = await signInWithEmailAndPassword(auth, email, password);

      return loging;
    } catch (error) {
      return null;
    }
  };

  const sendingPasswordResetMail = async (email: string) => {
    return await sendPasswordResetEmail(auth, email)
      .then(() => {
        return "Solicitud enviada correctamente";
      })
      .catch((error) => {
        return "Error enviando la solicitud";
      });
  };

  const updateEmailAuth = async (email: string) => {
    const authUser = getAuth();
    const currentUserAuth = authUser.currentUser;
    return await updateEmail(currentUserAuth, email);
  };
  function buildUserNotis(arr: any[]): IUserNotis {
    // Filtrar por tipo
    const notisByType = (type: string) => arr.filter((doc) => doc.type === type);

    const groupNotis = (docs: any[]) => ({
      viewed: docs.filter((doc) => doc.viewed === true),
      unviewed: docs.filter((doc) => doc.viewed === false),
    });

    return {
      canteen: groupNotis(notisByType("canteen")),
      eventsBmc: groupNotis(notisByType("events-bmc")),
      tickets: groupNotis(notisByType("tickets")),
      events: groupNotis(notisByType("events-business")),
      packages: groupNotis(notisByType("packages")),
      carsharing: groupNotis(notisByType("carsharing")),
      activitysharing: groupNotis(notisByType("activitysharing")),
      sala: groupNotis(notisByType("sala")),
      totalUnviewed: arr.filter((doc) => doc.viewed === false).length,
    };
  }
  const updateActualPassword = async (password: string, actualPassword: string, email: string) => {
    const authUser = getAuth();
    const currentUser = authUser.currentUser;
    const authCred = EmailAuthProvider.credential(email, actualPassword);

    // Reautentica al usuario y actualiza la contraseña
    await reauthenticateWithCredential(currentUser, authCred);
    await updatePassword(currentUser, password);

    // Si el usuario actual tiene "firstTime" en true, se actualiza a false en Firestore
    if (user && user.firstTime) {
      await updateDoc(doc(db, "users", user.id), { firstTime: false });

      // Actualiza el estado local y el caché para reflejar el cambio
      const updatedUser: IUser = { ...user, firstTime: false };
      setUser(updatedUser);
      localStorage.setItem(`user_${user.uid}`, JSON.stringify(updatedUser));
    }
  };

  const getUserNotifications = useCallback(() => {
    if (user?.id) {
      const q = query(
        collection(doc(collection(db, "users"), user.id), "notifications"),
        orderBy("createdAtt", "desc"),
        limit(50)
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const arr = querySnapshot.docs.map((d) => ({
          ...d.data(), // <- primero los datos
          id: d.id, // <- y al final el id REAL del doc de Firestore
        }));

        setUserNotis(buildUserNotis(arr));
      });

      // ✅ Devolvemos el unsubscribe para limpiar la suscripción
      return unsubscribe;
    }
    return null;
  }, [user?.id, db]);

  useEffect(() => {
    checkRoles();
    
    // ✅ Gestionar suscripción a notificaciones con cleanup
    const unsubscribe = getUserNotifications();
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user?.id, db, checkRoles, getUserNotifications]); // ✅ Dependencias correctas

  const signout = async () => {
    try {
      await signOut(auth);
      localStorage.setItem("token", "");
    } catch (error) {
      console.log(error);
    }
  };

  // ✅ Acepta string[] o {id:string}[] y espera a que acabe
  const updateNotifications = async (notisToUpdate: any[] = []) => {
    const tasks = notisToUpdate.filter((n) => n?.id).map((n) => updateDoc(doc(db, "users", user.id, "notifications", n.id), { viewed: true }));
    await Promise.all(tasks);
  };

  const deleteNotifications = async (notisToDelete: any[] = []) => {
    const tasks = notisToDelete.filter((n) => n?.id).map((n) => deleteDoc(doc(db, "users", user.id, "notifications", n.id)));
    await Promise.all(tasks);
  };

  const setMaintenanceMode = async (enabled: boolean) => {
    const maintenanceDocRef = doc(db, "config", "maintenance");
    await setDoc(maintenanceDocRef, { enabled, updatedAt: Date.now() });
  };

  // ============================================================
  // FUNCIONES PARA NOTIFICACIONES GLOBALES (Fase 2)
  // ============================================================

  /**
   * Crea una notificación global (eventos BMC, ofertas cafetería, etc.)
   * Llama a la Cloud Function que crea 1 documento en vez de 200k
   */
  const createGlobalNotification = async (
    type: string, 
    title: string, 
    description?: string, 
    image?: string, 
    data?: any, 
    expiresAt?: number,
    businessId?: string
  ) => {
    try {
      const createNotification = httpsCallable(functions, "createGlobalNotification");
      const result: any = await createNotification({
        type,
        title,
        description,
        image,
        data,
        expiresAt,
        businessId,
      });

      if (result.data.success) {
        console.log("✅ Notificación global creada:", result.data.notificationId);
      }
    } catch (error) {
      console.error("❌ Error creando notificación global:", error);
      throw error;
    }
  };

  /**
   * Marca una notificación global como vista para el usuario actual
   */
  const markGlobalNotificationAsViewed = async (notificationId: string) => {
    if (!user?.id) return;

    try {
      const stateRef = doc(db, "user-notification-state", `${user.id}_${notificationId}`);
      await setDoc(stateRef, {
        userId: user.id,
        notificationId,
        viewed: true,
        viewedAt: Date.now(),
        deleted: false,
      }, { merge: true });
    } catch (error) {
      console.error("Error marcando notificación como vista:", error);
    }
  };

  /**
   * "Elimina" una notificación global para el usuario actual
   * (realmente solo marca como deleted en user-notification-state)
   */
  const deleteGlobalNotificationForUser = async (notificationId: string) => {
    if (!user?.id) return;

    try {
      const stateRef = doc(db, "user-notification-state", `${user.id}_${notificationId}`);
      await setDoc(stateRef, {
        userId: user.id,
        notificationId,
        deleted: true,
        deletedAt: Date.now(),
      }, { merge: true });
    } catch (error) {
      console.error("Error eliminando notificación para usuario:", error);
    }
  };

  /**
   * Carga notificaciones globales activas y combina con el estado del usuario
   */
  const loadGlobalNotifications = useCallback(() => {
    if (!user?.id) return null;

    // Query de notificaciones globales activas
    const q = query(
      collection(db, "global-notifications"),
      orderBy("createdAt", "desc"),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const notificationsData = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      } as IGlobalNotification));

      // Cargar estados del usuario para estas notificaciones
      const statePromises = notificationsData.map(async (notif) => {
        const stateDoc = await getDoc(doc(db, "user-notification-state", `${user.id}_${notif.id}`));
        const stateData = stateDoc.data();

        return {
          ...notif,
          viewed: stateData?.viewed || false,
          deleted: stateData?.deleted || false,
        };
      });

      const notificationsWithState = await Promise.all(statePromises);

      // Filtrar las que el usuario no ha eliminado
      const activeNotifications = notificationsWithState.filter((n) => !n.deleted);

      setGlobalNotifications(activeNotifications);
    });

    return unsubscribe;
  }, [user?.id, db]);

  // Cargar notificaciones globales cuando el usuario se autentica
  useEffect(() => {
    const unsubscribe = loadGlobalNotifications();
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [loadGlobalNotifications]);

  return (
    <FirebaseContext.Provider
      value={{
        user,
        userNotis,
        updateNotifications,
        deleteNotifications,
        messaging,
        tokenMessaging,
        permissions,
        loadingUser,
        signup,
        signout,
        login,
        db,
        sendingPasswordResetMail,
        getUserData,
        uploadImage,
        progresspercent,
        allUsers,
        updateUser,
        updateEmailAuth,
        updateActualPassword,
        setNotification,
        notification,
        getUsersByBusinessId,
        getFirebaseNextUsers,
        getAllUsersByName,
        getFirebaseAllUsers,
        loadAllUsers,
        loadAllUsersForAdmin,
        addNotiEventBmc,
        createGlobalNotification,
        globalNotifications,
        markGlobalNotificationAsViewed,
        deleteGlobalNotificationForUser,
        updateAllUsers,
        isMaintenanceMode,
        setMaintenanceMode,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export const useAuthState = () => {
  const auth = useContext(FirebaseContext);
  return {
    ...auth,
    loadingUser: auth.loadingUser,
    isAuthenticated: auth.user != null,
  };
};
